import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import R from 'ramda';
import ReactPlayer from 'react-player';
import Controls from '../../shared/Controls';

/**
* @visibleName Video Player
*/

export const generateVideoPlayerClassNames = ({ playing, youtube, isYoutubeInConfig }) => classNames(
  'video-player',
  {
    'video-player--paused': !playing,
    'video-player--youtube': youtube,
    'video-player--file' : !isYoutubeInConfig,
  },
);

export const getPercentage = ({ progress }) =>
  Math.floor(parseFloat(R.prop('played', progress)).toFixed(3) * 100);

// istanbul ignore next
class VideoPlayer extends Component {
  componentDidMount() {
    const {
      muted = false,
      volume = 1,
      id,
      initVideo,
      setTogglePlayPause,
      autoPlay,
    } = this.props;

    initVideo({ muted, volume, id });

    if (autoPlay) {
      setTogglePlayPause({ id });
    }
  }

  onPlay = () => {
    const {
      id,
      videos,
      onPlay,
      videoData,
      setMediaPlay,
    } = this.props;
    const { url } = videoData;
    const duration = R.path([id, 'duration'])(videos);
    const progress = R.path([id, 'progress'])(videos);
    const mediaPlay = R.path([id, 'mediaPlay'])(videos);

    if (onPlay && !mediaPlay) {
      onPlay({
        url,
        duration,
        progress,
        ...this.getDefaultTitle(),
      });
      setMediaPlay({ id });
    }
  };

  onDuration = (duration) => {
    const {
      id,
      setDuration,
      onDuration,
      videoData,
    } = this.props;

    const { url } = videoData;
    const payload = {
      duration: parseInt(duration, 10),
      id,
      url,
      ...this.getDefaultTitle(),
    };

    setDuration(payload);

    if (onDuration) {
      onDuration(payload);
    }
  };

  onProgress = (progress) => {
    const {
      id,
      setProgress,
      onProgress,
      videoData,
      videos,
      resetMilestone,
      setMilestone,
    } = this.props;
    const { url } = videoData;
    const duration = R.path([id, 'duration'])(videos);
    const percentage = getPercentage({ progress });
    const {
      tracked25,
      tracked50,
      tracked75,
    } = R.path([id, 'milestones'])(videos);
    const videoMilestone = R.prop('milestone', videos[id]);
    const payload = {
      progress,
      id,
      url,
      duration,
      percentage,
      videoMilestone,
      ...this.getDefaultTitle(),
    };

    setProgress(payload);
    setMilestone(payload);

    if (onProgress) {
      if (tracked25 || tracked50 || tracked75) {
        onProgress(payload);
      }
      if (percentage > 96 && percentage <= 100) {
        onProgress({ ...payload, videoMilestone: 100 });
        resetMilestone(payload);
      }
    }
  };

  getVideoTitle = () => {
    const { title } = this.props;
    const currentPlayer = this.ref.current && this.ref.current.getInternalPlayer();

    if (currentPlayer && !title) {
      return currentPlayer.getVideoData().title;
    } else if (title) {
      return title;
    }

    return '';
  };

  getDefaultTitle = () =>
    this.props.shouldUseDefaultTitle && { title: this.getVideoTitle() };

  ref = React.createRef();

  render() {
    const {
      id,
      videoData,
      videos,
      playing,
      played = 0,
      seeking = false,
      loop = false,
      playsinline = false,
      showControls = true,
      setTogglePlayPause,
      setVolume,
      setToggleMuted,
      setSeeking,
      setStopped,
      videoContent,
      playIconSrc,
      pauseIconSrc,
      muteIconSrc,
      volumeIconSrc,
      playButtonPosition = 'bottomLeft',
      config,
      youtube,
      showOnlyMuteControls,
      playButtonIconWidth,
      playButtonIconHeight,
      light,
      posterImage,
      lightPlayIcon,
    } = this.props;

    const {
      url,
      poster,
    } = videoData || {};

    const isYoutubeInConfig = !!(config && config.hasOwnProperty('youtube'))

    return (
      <div className={generateVideoPlayerClassNames({ playing, youtube, isYoutubeInConfig })}>
        <ReactPlayer
          url={url}
          light={light || posterImage}
          loop={loop}
          playsinline={playsinline}
          width="100%"
          height="100%"
          playing={R.pathOr(playing, [id, 'playing'])(videos)}
          volume={R.path([id, 'volume'])(videos)}
          muted={R.path([id, 'muted'])(videos)}
          playIcon={
            <button
              className="react-player__play-icon"
              role="button"
              tabIndex={0}
              aria-label="Play video"
              onClick={(e) => {
                e.preventDefault();
                setTogglePlayPause({ id });
              }}
            >
              {lightPlayIcon}
            </button>
          }
          config={{
            file: {
              attributes: {
                poster,
              },
            },
            ...config,
          }}
          onProgress={this.onProgress}
          onDuration={this.onDuration}
          onEnded={() => setStopped({ playing: false, id })}
          ref={this.ref}
          onPlay={this.onPlay}
        />
        {showControls &&
          <Controls
            id={id}
            playing={R.pathOr(playing, [id, 'playing'])(videos)}
            muted={R.path([id, 'muted'])(videos)}
            volume={R.path([id, 'volume'])(videos)}
            played={R.pathOr(played, [id, 'progress', 'played'])(videos)}
            videoDuration={R.path([id, 'duration'])(videos)}
            seeking={R.pathOr(seeking, [id, 'seeking'])(videos)}
            setTogglePlayPause={setTogglePlayPause}
            setToggleMuted={setToggleMuted}
            setVolume={setVolume}
            setSeeking={setSeeking}
            playerEl={this.ref}
            controls={R.prop('controls')(videoContent)}
            playIconSrc={playIconSrc}
            pauseIconSrc={pauseIconSrc}
            muteIconSrc={muteIconSrc}
            volumeIconSrc={volumeIconSrc}
            playButtonPosition={playButtonPosition}
            showOnlyMuteControls={showOnlyMuteControls}
            playButtonIconWidth={playButtonIconWidth}
            playButtonIconHeight={playButtonIconHeight}
          />
        }
      </div>
    );
  }
}

VideoPlayer.propTypes = {
  id: PropTypes.string.isRequired,
  videoData: PropTypes.shape({
    url: PropTypes.string,
    poster: PropTypes.string,
  }),
  videos: PropTypes.shape({
    muted: PropTypes.bool,
    playing: PropTypes.bool,
    volume: PropTypes.number,
    videoDuration: PropTypes.number,
    played: PropTypes.number,
    seeking: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.string,
    ]),
  }),
  light: PropTypes.string,
  muted: PropTypes.bool,
  playing: PropTypes.bool,
  volume: PropTypes.number,
  played: PropTypes.number,
  seeking: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string,
  ]),
  loop: PropTypes.bool,
  playsinline: PropTypes.bool,
  showControls: PropTypes.bool,
  initVideo: PropTypes.func,
  setTogglePlayPause: PropTypes.func,
  setVolume: PropTypes.func,
  setToggleMuted: PropTypes.func,
  setProgress: PropTypes.func,
  setDuration: PropTypes.func,
  setSeeking: PropTypes.func,
  setStopped: PropTypes.func,
  setMilestone: PropTypes.func,
  resetMilestone: PropTypes.func,
  videoContent: PropTypes.shape({
    play: PropTypes.string,
    pause: PropTypes.string,
    mute: PropTypes.string,
    unmute: PropTypes.string,
  }),
  playIconSrc: PropTypes.string,
  pauseIconSrc: PropTypes.string,
  muteIconSrc: PropTypes.string,
  volumeIconSrc: PropTypes.string,
  playButtonPosition: PropTypes.oneOf(['topLeft', 'topRight', 'bottomRight', 'bottomLeft', 'middle']),
  autoPlay: PropTypes.bool,
  onPlay: PropTypes.func,
  onDuration: PropTypes.func,
  onProgress: PropTypes.func,
  config: PropTypes.objectOf(PropTypes.any),
  setMediaPlay: PropTypes.func,
  youtube: PropTypes.bool,
  title: PropTypes.string,
  shouldUseDefaultTitle: PropTypes.bool,
  showOnlyMuteControls: PropTypes.bool,
  playButtonIconWidth: PropTypes.number,
  playButtonIconHeight: PropTypes.number,
  posterImage: PropTypes.string,
  lightPlayIcon: PropTypes.node,
};

export default VideoPlayer;
